/**
 * iter1213 - PendingOpeningBalanceTaskBar
 *
 * Persistent, non-dismissible amber task strip that sits at the top of the
 * app whenever the current business has one or more Plaid-connected bank
 * accounts whose opening balance was deferred by the completeness gates.
 *
 * Behaviour:
 *   - Polls GET /banking/outstanding-tasks every 30s.
 *   - Renders one row per pending OB task.
 *   - No X button, no auto-hide. Only disappears when the task is closed
 *     server-side (OB actually posted, either via retry or user confirmation
 *     through this bar's modal).
 *   - Clicking "Confirm now" opens the ConfirmOpeningBalanceDialog for that
 *     bank account. On save, the modal closes and the bar re-fetches.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import apiClient from '../../utils/apiClient';
import ConfirmOpeningBalanceDialog from './ConfirmOpeningBalanceDialog';

export default function PendingOpeningBalanceTaskBar() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/banking/outstanding-tasks');
      const ob = (data?.tasks || []).filter(
        (t) => t.type === 'opening_balance_confirmation',
      );
      setTasks(ob);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const int = setInterval(fetchTasks, 30000);
    return () => clearInterval(int);
  }, [fetchTasks]);

  if (loading || tasks.length === 0) return null;

  return (
    <>
      <div
        className="bg-amber-50 border-b border-amber-300"
        data-testid="pending-ob-task-bar"
      >
        <div className="max-w-7xl mx-auto px-4 py-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-xs font-semibold text-amber-900">
              {tasks.length === 1
                ? 'Action required: confirm opening balance'
                : `Action required: confirm ${tasks.length} opening balances`}
            </p>
          </div>
          <div className="space-y-1">
            {tasks.map((t) => {
              const bank = t.bank_name || t.account_name || 'Bank account';
              const last4 = t.account_number
                ? ` (…${String(t.account_number).slice(-4)})`
                : '';
              return (
                <div
                  key={t.id}
                  className="flex items-center justify-between gap-3 bg-white/70 rounded-md px-3 py-1.5"
                  data-testid={`pending-ob-task-row-${t.bank_account_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-amber-900 truncate">
                      {bank}
                      {last4}
                    </p>
                    <p className="text-[11px] text-amber-800 truncate">
                      We couldn&apos;t safely derive it &mdash; please enter the balance on your bank
                      statement for the day before your accounting start date.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    onClick={() => setActiveTask(t)}
                    data-testid={`pending-ob-confirm-btn-${t.bank_account_id}`}
                  >
                    Confirm now <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ConfirmOpeningBalanceDialog
        task={activeTask}
        open={!!activeTask}
        onSaved={() => {
          setActiveTask(null);
          fetchTasks();
        }}
      />
    </>
  );
}

/**
 * Small pill that pages can drop next to a specific bank account card to
 * open the same dialog. Reuses the same task from /banking/outstanding-tasks
 * by matching bank_account_id.
 */
export function PendingOBAccountPill({ bankAccountId, onSaved }) {
  const [task, setTask] = useState(null);
  const [checking, setChecking] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!bankAccountId) { setChecking(false); return; }
    try {
      const { data } = await apiClient.get('/banking/outstanding-tasks');
      const t = (data?.tasks || []).find(
        (x) => x.type === 'opening_balance_confirmation'
          && x.bank_account_id === bankAccountId,
      );
      setTask(t || null);
    } catch { setTask(null); } finally { setChecking(false); }
  }, [bankAccountId]);

  useEffect(() => { load(); }, [load]);

  if (checking || !task) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200"
        data-testid={`pending-ob-pill-${bankAccountId}`}
      >
        {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3" />}
        Opening balance needed
      </button>
      <ConfirmOpeningBalanceDialog
        task={task}
        open={dialogOpen}
        onSaved={() => {
          setDialogOpen(false);
          setTask(null);
          onSaved?.();
        }}
      />
    </>
  );
}
